import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion, useMotionValue, useAnimationFrame } from 'framer-motion';
import { UserRound, UsersRound, ShoppingCart, Gift, Car, CalendarClock, CheckCheck, Check, Plane, UtensilsCrossed } from 'lucide-react';
type Audience = 'personal' | 'team';
type GalleryCard = {
  id: string;
  category: string;
  title: string;
  quote: string;
  gradient: string;
  icon: string;
  // Optional lucide icon node for the connector tile beneath the mockup. When
  // present it replaces the brand AppIcon — used for cards (like Errands) that
  // aren't about a single brand, so we show a clean concept icon instead.
  iconNode?: React.ReactNode;
  ui: React.ReactNode;
};
// ── FOR YOU ────────────────────────────────────────────────────────────────
// Personal life admin. Titles are the OUTCOME (the thing that ends up done),
// not the chore. Each quote is a real, specific ask in your own words.
const PERSONAL_CARDS: GalleryCard[] = [{
  id: 'travel-booked',
  category: 'Travel',
  title: 'Trip, fully booked',
  quote: '"Book my Friday trip to SF — window seat under $400, a hotel near the office, and put it all on my calendar."',
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'skyscanner',
  iconNode: <Plane size={18} strokeWidth={2.25} data-id="element-398" />,
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-399">
        <div className="flex items-center justify-between mb-3" data-id="element-400">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-401">
            SFO · Fri
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-402">$382</span>
        </div>
        <div className="flex items-center gap-2 mb-2" data-id="element-403">
          <div className="h-1.5 w-6 bg-neutral-300 rounded" data-id="element-404" />
          <div className="flex-1 h-px bg-neutral-200" data-id="element-405" />
          <div className="h-1.5 w-6 bg-neutral-300 rounded" data-id="element-406" />
        </div>
        <div className="h-1.5 w-2/3 bg-neutral-100 rounded" data-id="element-407" />
      </div>
}, {
  id: 'errands-handled',
  category: 'Errands',
  title: 'Errands, all done',
  quote: '"Reorder my usual groceries, renew my car registration, and reschedule the dentist for next week. Tell me when each is done."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'amazon',
  iconNode: <CheckCheck size={18} strokeWidth={2.25} data-id="element-408" />,
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-sm w-52 border border-white/40" data-id="element-409">
        <div className="flex items-center justify-between mb-3" data-id="element-410">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-411">
            Today's errands
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-412">3 of 3</span>
        </div>
        <div className="space-y-2" data-id="element-413">
          {[{
        icon: ShoppingCart,
        label: 'Groceries',
        status: 'Reordered'
      }, {
        icon: Car,
        label: 'Car registration',
        status: 'Renewed'
      }, {
        icon: CalendarClock,
        label: 'Dentist',
        status: 'Rebooked'
      }].map(({
        icon: Icon,
        label,
        status
      }) => <div key={label} className="flex items-center gap-2.5 rounded-lg bg-[#FBF6F0] px-2.5 py-1.5" data-id="element-414">
              <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-white text-[#EA580C] shrink-0 shadow-sm" data-id="element-415">
                <Icon size={12} strokeWidth={2.25} data-id="element-416" />
              </span>
              <span className="text-[10px] font-semibold text-neutral-800 leading-none flex-1 truncate" data-id="element-417">
                {label}
              </span>
              <span className="flex items-center gap-1 text-[9px] font-bold text-green-600 leading-none shrink-0" data-id="element-418">
                <Check size={10} strokeWidth={3} data-id="element-419" />
                {status}
              </span>
            </div>)}
        </div>
      </div>
}, {
  id: 'bills-paid',
  category: 'Finance',
  title: 'Bills, never late',
  quote: '"Watch my accounts, pay every recurring bill on time, and flag anything that looks off before it posts."',
  gradient: 'from-[#FBF4EC] to-[#F3E9DB]',
  icon: 'stripe',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-420">
        <div className="flex items-center justify-between mb-3" data-id="element-421">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-422">
            This month
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-423">On track</span>
        </div>
        <div className="space-y-2" data-id="element-424">
          {[true, true, true].map((paid, i) => <div key={i} className="flex items-center gap-2" data-id="element-425">
              <div className="w-3 h-3 rounded-full bg-[#EA580C] shrink-0" data-id="element-426" />
              <div className="h-1.5 flex-1 bg-neutral-100 rounded" data-id="element-427" />
            </div>)}
        </div>
      </div>
}, {
  id: 'inbox-zero',
  category: 'Inbox',
  title: 'Inbox, cleared by morning',
  quote: '"Triage my personal inbox overnight — unsubscribe from junk, draft replies I just need to approve, and surface only what matters."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'gmail',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-428">
        <div className="flex items-center justify-between mb-3" data-id="element-429">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-430">Inbox</span>
          <span className="text-[10px] font-bold text-[#EA580C]" data-id="element-431">
            2 to read
          </span>
        </div>
        <div className="space-y-2" data-id="element-432">
          {[1, 2].map(i => <div key={i} className="flex items-center gap-2" data-id="element-433">
              <div className="w-2 h-2 rounded-full bg-[#EA580C]/40 shrink-0" data-id="element-434" />
              <div className="h-1.5 flex-1 bg-neutral-100 rounded" data-id="element-435" />
            </div>)}
        </div>
      </div>
}, {
  id: 'gift-found',
  category: 'Ordering',
  title: 'The right gift, ordered',
  quote: "\"It's my mom's birthday next week — find something she'd love under $80, order it, and have it shipped to arrive on time.\"",
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'etsy',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-436">
        <div className="h-16 w-full rounded-lg bg-[#FFF1E8] mb-3 flex items-center justify-center text-[#EA580C]" data-id="element-437">
          <Gift size={24} strokeWidth={1.75} data-id="element-438" />
        </div>
        <div className="flex items-center justify-between" data-id="element-439">
          <div className="h-1.5 w-16 bg-neutral-200 rounded" data-id="element-440" />
          <span className="text-[10px] font-bold text-green-600" data-id="element-441">
            Arrives Tue
          </span>
        </div>
      </div>
}, {
  id: 'plans-made',
  category: 'Scheduling',
  title: 'Plans, locked in',
  quote: '"Find a great dinner spot for four near downtown Friday at 7, book it, and text everyone the details."',
  gradient: 'from-[#FBF1E7] to-[#FBDFC4]',
  icon: 'opentable',
  iconNode: <UtensilsCrossed size={18} strokeWidth={2.25} data-id="element-442" />,
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-443">
        <div className="flex items-center justify-between mb-3" data-id="element-444">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-445">
            Fri · 7:00
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-446">
            Table for 4
          </span>
        </div>
        <div className="h-1.5 w-full bg-neutral-100 rounded mb-1.5" data-id="element-447" />
        <div className="h-1.5 w-2/3 bg-neutral-100 rounded" data-id="element-448" />
      </div>
}];
// ── FOR YOUR TEAM ────────────────────────────────────────────────────────────
// Work outcomes a small team can't always staff for. Titles are the delivered
// result; quotes are how a teammate would actually ask in Slack.
const TEAM_CARDS: GalleryCard[] = [{
  id: 'prep-sales-call',
  category: 'Sales',
  title: 'Sales calls, prepped',
  quote: '"Prep the team for tomorrow\'s Acme call — pull the HubSpot deal, last email threads, and a one-pager on where it stands."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'hubspot',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-449">
        <div className="h-2 w-16 bg-neutral-200 rounded mb-3" data-id="element-450" />
        <div className="flex justify-between items-center mb-2" data-id="element-451">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-452">
            Acme Corp
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-453">$84k ARR</span>
        </div>
        <div className="h-1.5 w-full bg-neutral-100 rounded mb-1.5" data-id="element-454" />
        <div className="h-1.5 w-3/4 bg-neutral-100 rounded" data-id="element-455" />
      </div>
}, {
  id: 'board-decks',
  category: 'Finance',
  title: 'Board deck, investor-ready',
  quote: '"Refresh the board deck with the latest numbers — MRR from Stripe, burn from QuickBooks, pipeline from Salesforce."',
  gradient: 'from-[#FBF1E7] to-[#FBDFC4]',
  icon: 'stripe',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-456">
        <div className="flex justify-between items-center mb-3" data-id="element-457">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-458">
            MRR Growth
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-459">+18%</span>
        </div>
        <div className="h-16 w-full flex items-end gap-1" data-id="element-460">
          {[40, 55, 45, 70, 65, 85, 100].map((h, i) => <div key={i} className="flex-1 bg-[#EA580C]/25 rounded-t-sm" style={{
        height: `${h}%`
      }} data-id="element-461" />)}
        </div>
      </div>
}, {
  id: 'pipeline-current',
  category: 'CRM',
  title: 'CRM, always current',
  quote: '"After every call and email, update the deal stage, log the notes, and create the follow-up tasks in Salesforce."',
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'salesforce',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-462">
        <div className="h-2 w-20 bg-neutral-800 rounded mb-3" data-id="element-463" />
        <div className="space-y-2" data-id="element-464">
          {['Qualified', 'Proposal', 'Won'].map((_, i) => <div key={i} className="flex items-center gap-2" data-id="element-465">
              <div className="w-3 h-3 rounded-full bg-[#EA580C] shrink-0" data-id="element-466" />
              <div className="h-1.5 flex-1 bg-neutral-100 rounded" data-id="element-467" />
            </div>)}
        </div>
      </div>
}, {
  id: 'reports-shipped',
  category: 'Reporting',
  title: 'Reports, shipped on schedule',
  quote: '"Every Monday at 8am, compile last week\'s metrics into the standup doc and post the summary to #leadership."',
  gradient: 'from-[#FBF4EC] to-[#F3E9DB]',
  icon: 'slack',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-468">
        <div className="flex items-center justify-between mb-3" data-id="element-469">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-470">
            Weekly report
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-471">Sent</span>
        </div>
        <div className="h-1.5 w-full bg-neutral-100 rounded mb-1.5" data-id="element-472" />
        <div className="h-1.5 w-5/6 bg-neutral-100 rounded mb-1.5" data-id="element-473" />
        <div className="h-1.5 w-2/3 bg-neutral-100 rounded" data-id="element-474" />
      </div>
}, {
  id: 'tickets-triaged',
  category: 'Support',
  title: 'Support queue, triaged',
  quote: '"Sort the support queue — tag by urgency, draft replies for the routine ones, and escalate anything at risk of breaching SLA."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'zendesk',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-475">
        <div className="flex items-center justify-between mb-3" data-id="element-476">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-477">Open</span>
          <span className="text-[10px] font-bold text-[#EA580C]" data-id="element-478">4 urgent</span>
        </div>
        <div className="space-y-2" data-id="element-479">
          {[1, 2, 3].map(i => <div key={i} className="flex items-center gap-2" data-id="element-480">
              <div className="w-2 h-2 rounded-full bg-[#EA580C]/40 shrink-0" data-id="element-481" />
              <div className="h-1.5 flex-1 bg-neutral-100 rounded" data-id="element-482" />
            </div>)}
        </div>
      </div>
}, {
  id: 'new-hires-ramped',
  category: 'Onboarding',
  title: 'New hires, ramped in week one',
  quote: '"Set up our new AE — build their onboarding doc, grant tool access, and queue intro messages to the accounts they\'re inheriting."',
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'notion',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-483">
        <div className="h-2 w-24 bg-neutral-800 rounded mb-4" data-id="element-484" />
        <div className="space-y-2.5" data-id="element-485">
          {[true, true, false].map((done, i) => <div key={i} className="flex items-center gap-2" data-id="element-486">
              <div className={`w-3 h-3 rounded-sm shrink-0 ${done ? 'bg-[#EA580C]' : 'border border-neutral-300'}`} data-id="element-487" />
              <div className="h-1.5 w-full bg-neutral-200 rounded" data-id="element-488" />
            </div>)}
        </div>
      </div>
}];
function AppIcon({
  slug,
  className
}: {
  slug: string;
  className?: string;
}) {
  return <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`} alt="" aria-hidden="true" className={className} onError={e => {
    const img = e.currentTarget as HTMLImageElement;
    if (!img.src.includes('simpleicons')) {
      img.src = `https://cdn.simpleicons.org/${slug.replace(/-/g, '')}`;
    } else {
      img.style.visibility = 'hidden';
    }
  }} data-id="element-489" />;
}
function GalleryJobCard({
  card,
  audience
}: {
  card: GalleryCard;
  audience: Audience;
}) {
  const isTeam = audience === 'team';
  return <div className="w-[280px] md:w-[320px] shrink-0 flex flex-col gap-6" data-id="element-490">
      {/* Visual Area */}
      <div className={`h-[340px] rounded-3xl bg-gradient-to-br ${card.gradient} p-6 relative overflow-hidden flex flex-col items-center justify-center`} data-id="element-491">
        <div className="relative z-10 flex flex-col items-center" data-id="element-492">
          {card.ui}
          <div className="w-px h-12 bg-black/10 my-2" data-id="element-493" />
          <div className="w-10 h-10 rounded-full bg-white/80 backdrop-blur-md shadow-sm border border-white flex items-center justify-center text-[#EA580C]" data-id="element-494">
            {card.iconNode ?? <AppIcon slug={card.icon} className="w-5 h-5 object-contain" data-id="element-495" />}
          </div>
        </div>
        <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay pointer-events-none" style={{
        backgroundImage: 'url("https://grainy-gradients.vercel.app/noise.svg")'
      }} data-id="element-496" />
      </div>

      {/* Text Area */}
      <div className="flex flex-col gap-3 px-2" data-id="element-497">
        <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase" data-id="element-498">
          <span className="w-1.5 h-1.5 rounded-full bg-[#EA580C]" data-id="element-499" />
          <span className="text-[#EA580C]" data-id="element-500">{card.category}</span>
        </span>
        <h3 className="text-xl font-bold text-[#2B211A] -mt-1" data-id="element-501">{card.title}</h3>
        <p className="text-sm text-neutral-600 leading-relaxed" data-id="element-502">{card.quote}</p>
      </div>
    </div>;
}
function CardMarquee({
  cards,
  audience,
  reduceMotion
}: {
  cards: GalleryCard[];
  audience: Audience;
  reduceMotion: boolean | null;
}) {
  if (reduceMotion) {
    return <div className="flex overflow-x-auto gap-6 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-503">
        {cards.map(card => <GalleryJobCard key={card.id} card={card} audience={audience} data-id="element-504" />)}
      </div>;
  }
  return <DraggableMarquee cards={cards} audience={audience} data-id="element-505" />;
}
// Auto-advancing AND draggable marquee. The track auto-scrolls continuously;
// the user can grab and drag it left/right at any point, and it resumes
// auto-scrolling from wherever they release. Three identical copies of the card
// set are rendered so there's always content buffering both sides during a
// drag, and the x position is wrapped into the middle band every frame for a
// seamless infinite loop.
function DraggableMarquee({
  cards,
  audience
}: {
  cards: GalleryCard[];
  audience: Audience;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const draggingRef = useRef(false);
  // Width of ONE card set (the track holds three identical sets).
  const [setWidth, setSetWidth] = useState(0);
  // Auto-scroll speed (px/sec). Faster on mobile, where the user is more
  // likely to just watch the cards drift by than grab and drag them.
  const [speed, setSpeed] = useState(50);
  // Measure a single set's width, then park x in the middle copy so there's a
  // full set of cards buffering both the left and right during drags.
  useEffect(() => {
    const measure = () => {
      const el = trackRef.current;
      if (!el) return;
      const w = el.scrollWidth / 3;
      setSetWidth(w);
      x.set(-w);
      setSpeed(window.innerWidth < 640 ? 95 : 55);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [cards, x]);
  // Wrap x into the middle band so the loop stays seamless and always filled.
  const wrapMid = (v: number) => {
    if (!setWidth) return v;
    const mod = ((v + 2 * setWidth) % setWidth + setWidth) % setWidth;
    return -2 * setWidth + mod;
  };
  useAnimationFrame((_, delta) => {
    if (draggingRef.current || !setWidth) return;
    // Move the track left→right (x increasing); wrapMid keeps it seamless.
    const next = x.get() + speed * delta / 1000;
    x.set(wrapMid(next));
  });
  return (
    // No edge fade on mobile (the strong left fade looked heavy on narrow
    // screens) — it reads as a clean carousel there. The soft side fades only
    // kick in from the sm breakpoint up.
    <div className="relative w-full overflow-hidden sm:[mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]" data-id="element-506">
      <motion.div ref={trackRef} className="flex gap-6 w-max cursor-grab active:cursor-grabbing select-none touch-pan-y" style={{
        x
      }} drag="x" dragMomentum={false} dragElastic={0.04} onDragStart={() => {
        draggingRef.current = true;
      }} onDragEnd={() => {
        // Snap back into the seamless middle band, then let the auto-scroll
        // frame loop take over again from this position.
        x.set(wrapMid(x.get()));
        draggingRef.current = false;
      }} data-id="element-507">
        {[...cards, ...cards, ...cards].map((card, i) => <GalleryJobCard key={`${card.id}-${i}`} card={card} audience={audience} data-id="element-508" />)}
      </motion.div>
    </div>
  );
}
export function AudienceSwitch() {
  const reduceMotion = useReducedMotion();
  const [audience, setAudience] = useState<Audience>('personal');
  const cards = audience === 'team' ? TEAM_CARDS : PERSONAL_CARDS;
  return <section className="w-full bg-[#FAF8F4] py-24 md:py-32 border-b border-neutral-200/70 relative overflow-hidden" data-id="element-509">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 relative" data-id="element-510">
        {/* Section opening */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-10 md:mb-12 max-w-3xl mx-auto text-center" data-id="element-511">
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] mb-5 md:mb-6" data-id="element-512">
            One Giga. Two ways to put it to work.
          </h2>
          <p className="text-base md:text-lg text-neutral-500 font-medium leading-relaxed max-w-2xl mx-auto" data-id="element-513">
            The same assistant moves between your business and your personal
            life. Pick a side to see exactly what it gets done.
          </p>
        </motion.div>

        {/* Audience toggle */}
        <div className="flex justify-center mb-12 md:mb-16" data-id="element-514">
          <div role="tablist" aria-label="Choose audience" className="relative grid grid-cols-2 gap-1 p-1.5 w-full max-w-[360px] rounded-full border border-neutral-200/70 bg-white shadow-sm" data-id="element-515">
            <motion.div aria-hidden="true" className="absolute inset-y-1.5 w-[calc(50%-6px)] rounded-full bg-[#FAF8F4] border border-neutral-200/80 shadow-sm" animate={{
            left: audience === 'personal' ? '6px' : 'calc(50%)'
          }} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30
          }} data-id="element-516" />
            <button role="tab" aria-selected={audience === 'personal'} onClick={() => setAudience('personal')} className={`relative z-10 flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 ${audience === 'personal' ? 'text-[#2B211A]' : 'text-neutral-500 hover:text-[#2B211A]'}`} data-id="element-517">
              <UserRound size={16} strokeWidth={2.5} className={audience === 'personal' ? 'text-[#EA580C]' : 'text-neutral-400'} data-id="element-518" />
              <span className="whitespace-nowrap" data-id="element-519">For you</span>
            </button>
            <button role="tab" aria-selected={audience === 'team'} onClick={() => setAudience('team')} className={`relative z-10 flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 ${audience === 'team' ? 'text-[#2B211A]' : 'text-neutral-500 hover:text-[#2B211A]'}`} data-id="element-520">
              <UsersRound size={16} strokeWidth={2.5} className={audience === 'team' ? 'text-[#EA580C]' : 'text-neutral-400'} data-id="element-521" />
              <span className="whitespace-nowrap" data-id="element-522">For your team</span>
            </button>
          </div>
        </div>

        {/* Separate carousel per audience */}
        <div className="w-full overflow-hidden" data-id="element-523">
          <AnimatePresence mode="wait" data-id="element-524">
            <motion.div key={audience} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.35,
            ease: [0.16, 1, 0.3, 1]
          }} data-id="element-525">
              <CardMarquee cards={cards} audience={audience} reduceMotion={reduceMotion} data-id="element-526" />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
}